import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './KebabMenu.css';
import { IconCheck } from '../../../assets/icons/IconCheck';
import IconChecked from '../../../assets/icons/IconChecked';
import IconUnchecked from '../../../assets/icons/IconUnchecked';
import { Box, Button } from '@mui/material';

interface Option {
  id: string;
  label: string;
  subOptions?: Option[];
  multiSelect?: boolean;
  prefixIcon?: JSX.Element;
}

const KebabMenu = ({ options, disabled, isMenuOpen, setIsMenuOpen }: any) => {
  const kebabButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null); // Ref for the menu
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const [activeBaseOption, setActiveBaseOption] = useState<string | null>(null);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  useEffect(() => {
    if (!isMenuOpen) {
      setSelectedValues([]);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !kebabButtonRef.current?.contains(event.target as Node)
      ) {
        setIsMenuOpen(false); // Close menu if clicked outside
      }
    };

    const handleScroll = (event: Event) => {
      if (menuRef.current && menuRef.current.contains(event.target as Node)) {
        // Prevent closing the menu if scrolling inside the menu
        return;
      }
      setIsMenuOpen(false); // Close menu on scroll if outside the menu
    };

    const handleWheel = (event: WheelEvent) => {
      if (menuRef.current && menuRef.current.contains(event.target as Node)) {
        // Prevent closing the menu if wheel is used inside the menu
        return;
      }
      setIsMenuOpen(false); // Close menu on wheel scroll if outside the menu
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (menuRef.current && menuRef.current.contains(event.target as Node)) {
        // Prevent closing the menu if touch move is inside the menu
        return;
      }
      setIsMenuOpen(false); // Close menu on touch move if outside the menu
    };

    // Add event listeners for mouse click, scroll, wheel, and touchmove
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('wheel', handleWheel, { passive: true });
    globalThis.addEventListener('touchmove', handleTouchMove, {
      passive: true,
    });

    // Cleanup event listeners when component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleWheel);
      globalThis.removeEventListener('touchmove', handleTouchMove);
    };
  }, [setIsMenuOpen]);

  const handleHover = (id: string) => {
    setActiveBaseOption(id);
  };

  const handleMouseLeave = () => {
    setActiveBaseOption(null);
  };

  return (
    <>
      {isMenuOpen && (
        <div className='overlay' onClick={() => setIsMenuOpen(false)} />
      )}
      <button
        className='kebab-menu kebab-icon'
        onClick={() => setIsMenuOpen((prev: boolean) => !prev)}
        ref={kebabButtonRef}
        style={{
          opacity: disabled ? 0 : 1,
          cursor: disabled ? 'default' : undefined,
          width: disabled ? 0 : undefined,
          height: disabled ? 0 : undefined,
        }}
        disabled={disabled}
      >
        &#8942;
      </button>
      {isMenuOpen && (
        <Menu
          menuRef={menuRef} // Pass menuRef to the Menu component
          menuStyle={menuStyle}
          options={options}
          handleHover={handleHover}
          handleMouseLeave={handleMouseLeave}
          activeBaseOption={activeBaseOption}
          onSelectValue={(value: string) => {
            setSelectedValues((prev) => {
              const updatedArray = new Set(prev);
              if (updatedArray.has(value)) {
                updatedArray.delete(value);
              } else {
                updatedArray.add(value);
              }
              return Array.from(updatedArray);
            });
          }}
          selectedOptions={selectedValues}
          kebabButtonRef={kebabButtonRef}
          setMenuStyle={setMenuStyle}
          hide={() => {
            handleMouseLeave();
            setIsMenuOpen(false);
          }}
        />
      )}
    </>
  );
};

type OptionMenuProp = {
  id: string | number;
  onClick?: () => void;
  prefixIcon: JSX.Element;
  label: string;
  multiselect: boolean;
  subOptions: SubOptionsType[];
  disabled?: boolean;
  onMultiselectDismiss: (values: string[]) => void;
};

type SubOptionsType = {
  id: string;
  label: string;
  selected?: boolean;
  subtitle?: string;
  required?: boolean;
  onClick?: () => void;
};

const Menu = ({
  menuStyle,
  options,
  handleHover,
  handleMouseLeave,
  activeBaseOption,
  onSelectValue,
  selectedOptions,
  kebabButtonRef,
  setMenuStyle,
  hide,
  menuRef, // New prop for menuRef
}: any) => {
  const [submenuStyles, setSubmenuStyles] = useState<
    Record<string, React.CSSProperties>
  >({});

  const calculateSubmenuPosition = (parentElement: HTMLElement) => {
    const { top, left, right, bottom, height, width } =
      parentElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const submenuWidth = 200; // Approximate submenu width
    const submenuHeight = 300; // Approximate submenu height

    const styles: React.CSSProperties = {};

    // Space calculations
    const spaceRight = viewportWidth - right;
    const spaceLeft = left;
    const spaceBelow = viewportHeight - bottom;
    const spaceAbove = top;

    // Check horizontal positioning
    if (spaceRight >= submenuWidth) {
      styles.left = `${width}px`; // Open right
    } else if (spaceLeft >= submenuWidth) {
      styles.right = `${width}px`; // Open left
    } else {
      // Default to right if both left and right have limited space
      styles.left = `${width}px`;
    }

    // Check vertical positioning
    if (spaceBelow >= submenuHeight) {
      styles.top = `0px`; // Open below
    } else if (spaceAbove >= submenuHeight) {
      styles.bottom = `0px`; // Open above
    } else {
      // Limited space, choose the better option
      if (spaceBelow > spaceAbove) {
        styles.top = `0px`;
      } else {
        styles.bottom = `0px`;
      }
    }

    return styles;
  };

  const handleSubmenuHover = (id: string, parentRef: HTMLElement) => {
    const positionStyles = calculateSubmenuPosition(parentRef);
    setSubmenuStyles((prevStyles) => ({
      ...prevStyles,
      [id]: positionStyles,
    }));
  };

  useEffect(() => {
    if (kebabButtonRef.current) {
      const { x, y, height, width } =
        kebabButtonRef.current.getBoundingClientRect();
      const menuHeight = 250; // Approximate height of the menu
      const menuWidth = 200; // Approximate width of the menu

      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Available space above and below
      const spaceBelow = viewportHeight - (y + height);
      const spaceAbove = y;

      let menuTop: number | undefined;
      let menuBottom: number | undefined;

      if (spaceBelow >= menuHeight) {
        // Enough space below
        menuTop = y + height + window.scrollY;
      } else if (spaceAbove >= menuHeight) {
        // Not enough space below, place menu above
        menuBottom = viewportHeight - y + window.scrollY;
      } else {
        // Limited space, choose the best fit
        if (spaceBelow > spaceAbove) {
          menuTop = y + height + window.scrollY;
        } else {
          menuBottom = viewportHeight - y + window.scrollY;
        }
      }

      // Prevent horizontal overflow
      let menuLeft = x;
      if (menuLeft + menuWidth > viewportWidth) {
        menuLeft = viewportWidth - menuWidth - 10;
      }

      setMenuStyle({
        top: menuTop ? `${menuTop}px` : undefined,
        bottom: menuBottom ? `${menuBottom}px` : undefined,
        left: `${menuLeft}px`,
      });
    }
  }, [kebabButtonRef, setMenuStyle]);

  const onClickSubmenu = (e: any, subOption: SubOptionsType) => {
    if (subOption.onClick) {
      subOption?.onClick();
      hide();
    } else {
      if (e) {
        e.stopPropagation();
      }
      onSelectValue(subOption.id);
    }
  };

  return ReactDOM.createPortal(
    <div className='menu' style={menuStyle} ref={menuRef}>
      {/* Added ref to the menu */}
      {options
        .filter((option: OptionMenuProp | any) => !option.disabled)
        .map((option: OptionMenuProp | any) => (
          <div
            key={option.id}
            className='menu-item'
            onMouseEnter={(e) => {
              handleHover(option.id);
              handleSubmenuHover(option.id, e.currentTarget);
            }}
            onMouseLeave={(e) => {
              if (option.onMultiselectDismiss && selectedOptions.length > 0) {
                option.onMultiselectDismiss(selectedOptions);
                hide();
              } else {
                handleMouseLeave(e);
              }
            }}
            onClick={option.onClick}
          >
            {option?.prefixIcon || null}
            <span>{option.label}</span>
            {option.subOptions && activeBaseOption === option.id && (
              <div
                className='sub-options'
                style={submenuStyles[option.id] || { left: '0px', top: '0px' }}
              >
                {option?.multiselect && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      onClick={(e) => {
                        if (
                          option.onMultiselectDismiss &&
                          selectedOptions.length > 0
                        ) {
                          option.onMultiselectDismiss(selectedOptions);
                          hide();
                        } else {
                          handleMouseLeave(e);
                        }
                      }}
                      variant='text'
                      size='small'
                      sx={{
                        minWidth: 'auto', // Prevent unnecessary width
                        padding: '4px 8px',
                        fontSize: '0.875rem',
                        textTransform: 'none', // Maintain natural casing
                        lineHeight: 'normal',
                      }}
                    >
                      CLOSE
                    </Button>
                  </Box>
                )}
                {option.subOptions.map((subOption: SubOptionsType) => (
                  <div
                    key={subOption.id}
                    className='sub-option'
                    onClick={(e) => onClickSubmenu(e, subOption)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '15px',
                      borderBottom: '1px solid #f0f0f0', // Optional divider
                    }}
                  >
                    <Box sx={{ marginBottom: '-5px' }}>
                      {selectedOptions.includes(subOption.id) ? (
                        <IconChecked />
                      ) : (
                        <IconUnchecked />
                      )}
                    </Box>
                    <div style={{ flex: 1, marginLeft: '15px' }}>
                      <div style={{ fontWeight: 500, fontSize: '12px' }}>
                        {subOption.label}
                      </div>
                    </div>
                    <div
                      style={{
                        color: '#7b7b7b',
                        fontSize: '10px',
                        marginLeft: '20px',
                      }}
                    >
                      {subOption.subtitle?.toLowerCase() === 'resolved' ? (
                        <IconCheck />
                      ) : (
                        subOption.subtitle
                      )}
                      {subOption.required && (
                        <span style={{ color: '#c85a5a' }}> *</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
    </div>,
    document.body
  );
};

export default KebabMenu;
